import { useMemo, useRef, useState } from 'react';
import { Send, Pencil, X } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { recordContactAttempt } from '@/domain/sequenceFlow';
import { suggestReply } from '@/domain/suggestedReply';
import { customerName } from '@/utils/format';
import { dmSendMode } from '@/domain/dmSendMode';
import { sendDmIntent, sendErrorCopy } from '@/api/intents';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import type { Channel, ConversationEntry, CustomerRecord } from '@/domain/types';

const CHANNEL_LABEL: Partial<Record<Channel, string>> = {
  sms: 'SMS',
  'meta-dm': 'Meta DM',
  'ig-dm': 'Instagram DM',
  email: 'email',
};

interface Props {
  customer: CustomerRecord;
  /** Channel the message would go out on, mirroring the composer's selection. */
  channel: Channel;
  /** Hands the body to the composer so the agent can edit before sending. */
  onEdit: (body: string) => void;
}

/**
 * The one message most worth sending right now, offered for approval. The body
 * is never generated - it comes from copy that already sends elsewhere in the
 * app (see domain/suggestedReply), so approving it sends a real studio message.
 */
export default function SuggestedReplyCard({ customer, channel, onEdit }: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const waivers = useBrainStore((s) => s.waivers);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const addConvo = useBrainStore((s) => s.addConversationEntry);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const sequence = useBrainStore((s) => s.sequenceState[customer.id]);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const nowIso = useSimulationStore((s) => s.clockIso);
  const [dismissed, setDismissed] = useState(false);
  // Synchronous in-flight guard: a double-click lands before a re-render could
  // disable the button (mirrors CustomerComposer's guard).
  const inFlight = useRef(false);

  const suggestion = useMemo(
    () =>
      suggestReply({
        customer,
        bookings,
        deposits,
        pieces,
        waivers,
        artists,
        studios,
        nowIso,
      }),
    [customer, bookings, deposits, pieces, waivers, artists, studios, nowIso],
  );

  if (!suggestion || dismissed) return null;

  const channelLabel = CHANNEL_LABEL[channel] ?? 'SMS';

  // Wall time, not the sim clock: Meta's reply windows are real time (tv-isee).
  const dm = isStoreFallbackEnabled()
    ? { mode: 'simulated' as const, hoursLeft: 0 }
    : dmSendMode(customer, channel, new Date().toISOString());
  const suggestionTemplateId = `suggestion:${suggestion.kind}`;

  // Outside 24h a person must review before a human-agent (or handoff) send: hand
  // the text to the composer through the card's existing onEdit seam, never send
  // from here (tv-isee policy: never one-click outside the reply window).
  const useReply = () => {
    onEdit(suggestion.body);
    setDismissed(true);
  };

  // Inside 24h a one-click real send is allowed, going through the same
  // send-dm intent (and idempotency guard) the composer uses.
  const sendReal = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const clientMessageId = crypto.randomUUID();
    try {
      const res = await sendDmIntent({
        customerId: customer.id,
        text: suggestion.body,
        path: 'reply',
        clientMessageId,
        templateId: suggestionTemplateId,
      });
      if (!res.ok) {
        pushNotification({ intensity: 'high', title: 'Not sent', body: sendErrorCopy(res), ttlMs: 5000 });
        return;
      }
      if (res.deliveryStatus === 'sending') {
        pushNotification({ intensity: 'info', title: 'Sending…', body: customerName(customer), ttlMs: 3500 });
      }
      const already = useBrainStore.getState().customers[customer.id]?.conversation.some((e) => e.id === res.entry.id);
      if (!already) {
        withServerAuthoredWrites([['customers', customer.id]], () => addConvo(customer.id, res.entry));
      }
      if (sequence) recordContactAttempt(customer.id);
      setDismissed(true);
    } finally {
      inFlight.current = false;
    }
  };

  // Same send path the composer uses, so a suggested send counts as a contact
  // attempt and the sequence advances on no reply exactly as it would otherwise.
  const onSend = () => {
    const entry: ConversationEntry = {
      id: `msg-suggested-${suggestion.kind}-${nowIso}`,
      customerId: customer.id,
      channel,
      direction: 'out',
      body: suggestion.body,
      timestamp: nowIso,
      authorRole: 'agent',
      authorName: 'You',
    };
    addConvo(customer.id, entry);
    appendActivity({
      id: `act-${entry.id}`,
      type: channel === 'sms' ? 'sms-sent' : 'meta-dm',
      customerId: customer.id,
      body: `${channelLabel} sent to ${customerName(customer)} (${suggestion.label.toLowerCase()}).`,
      timestamp: nowIso,
    });
    pushNotification({
      intensity: 'info',
      title: `${channelLabel} sent`,
      body: `${suggestion.label} to ${customerName(customer)}.`,
      ttlMs: 3500,
    });
    if (sequence) recordContactAttempt(customer.id);
    setDismissed(true);
  };

  return (
    <section
      className="rounded-lg border border-gold-bright/50 bg-ink-black px-4 py-3"
      data-testid="qwm-suggested"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="label-small-caps text-gold-bright">Suggested reply</p>
        <span className="pill-cream text-[10px]" data-testid="qwm-suggested-label">
          {suggestion.label}
        </span>
      </div>
      <p
        className="rounded-md bg-ink-dark px-3 py-2.5 font-serif text-sm text-cream m-0"
        data-testid="qwm-suggested-body"
      >
        {suggestion.body}
      </p>
      <p className="mt-2 font-inter text-[10px] text-cream-faint m-0">{suggestion.why}</p>
      {dm.mode !== 'simulated' && dm.mode !== 'reply' && (
        <p className="mt-1 font-inter text-[10px] text-cream-faint m-0">
          After 24 hours, suggestions go into the message box so you can check them first.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {dm.mode === 'simulated' && (
          <button
            type="button"
            onClick={onSend}
            className="btn-primary text-xs"
            data-testid="qwm-suggested-send"
          >
            <Send size={12} /> Send on {channelLabel}
          </button>
        )}
        {dm.mode === 'reply' && (
          <button
            type="button"
            onClick={() => void sendReal()}
            className="btn-primary text-xs"
            data-testid="qwm-suggested-send"
          >
            <Send size={12} /> Send
          </button>
        )}
        {(dm.mode === 'human_agent' || dm.mode === 'handoff') && (
          <button
            type="button"
            onClick={useReply}
            className="btn-primary text-xs"
            data-testid="qwm-suggested-use-reply"
          >
            <Pencil size={12} /> Use reply
          </button>
        )}
        {dm.mode === 'simulated' && (
          <button
            type="button"
            onClick={() => {
              onEdit(suggestion.body);
              setDismissed(true);
            }}
            className="btn-ghost text-xs"
            data-testid="qwm-suggested-edit"
          >
            <Pencil size={12} /> Edit first
          </button>
        )}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="btn-quiet text-[11px]"
          data-testid="qwm-suggested-dismiss"
        >
          <X size={12} /> Dismiss
        </button>
      </div>
    </section>
  );
}
